import { cacheKeyFor, type Store } from '@warp-drive/core';
import type {
  CollectionRelationship,
  ResourceRelationship,
} from '@warp-drive/core/types/cache/relationship';
import type { ResourceKey } from '@warp-drive/core/types/identifier';

/**
 * Options for controlling the behavior of deepHasChanged.
 */
export interface DeepHasChangedOptions {
  /**
   * Maximum depth to traverse relationships. Prevents infinite recursion in deeply nested structures.
   * Default: Infinity
   */
  maxDepth?: number;

  /**
   * Internal tracking of visited records to prevent circular reference loops.
   * Should not be set by external callers.
   * @internal
   */
  visited?: Set<string>;
}

/**
 * Creates a unique identifier for a record based on its cache key.
 */
function getCacheKeyId(cacheKey: ResourceKey): string {
  return `${cacheKey.type}:${cacheKey.id ?? 'null'}`;
}

/**
 * Deeply checks if a record or any of its related records have changed.
 *
 * This function:
 * 1. Checks if the record has changed attributes or relationships
 * 2. Traverses all relationships
 * 3. Recursively checks related records for changes
 * 4. Returns true if any record in the graph has changes
 *
 * @param record - The record to check
 * @param store - The Warp Drive store instance
 * @param options - Optional configuration for checking behavior
 * @returns true if the record or any related record has changes, false otherwise
 *
 * @example
 * ```typescript
 * // Simple check
 * if (deepHasChanged(cohort, this.store)) {
 *   // Handle unsaved changes
 * }
 *
 * // Limit depth to avoid deep traversal
 * if (deepHasChanged(cohort, this.store, { maxDepth: 2 })) {
 *   // Handle unsaved changes
 * }
 * ```
 */
export function deepHasChanged(
  record: unknown,
  store: Store,
  options: DeepHasChangedOptions = {}
): boolean {
  const { maxDepth = Infinity, visited = new Set<string>() } = options;

  // Get the cache key for this record
  const cacheKey = cacheKeyFor(record);
  const cacheKeyId = getCacheKeyId(cacheKey);

  // Prevent circular references
  if (visited.has(cacheKeyId)) {
    return false;
  }
  visited.add(cacheKeyId);

  // Check if this record has changed attributes
  const changedAttrs = store.cache.changedAttrs(cacheKey);
  if (changedAttrs && Object.keys(changedAttrs).length > 0) {
    return true;
  }

  // Check if this record has changed relationships
  const changedRelationships = store.cache.changedRelationships(cacheKey);
  if (changedRelationships.size > 0) {
    return true;
  }

  // Check if this is a new record (unsaved)
  if (
    typeof record === 'object' &&
    record !== null &&
    'isNew' in record &&
    record.isNew === true
  ) {
    return true;
  }

  // Early return if we've reached max depth for traversing relationships
  if (maxDepth <= 0) {
    return false;
  }

  // Get all fields from the schema
  const fields = store.schema.fields(cacheKey);

  // Traverse all relationships to check for changes in related records
  for (const [fieldName, field] of fields.entries()) {
    // Only process relationship fields
    if (field.kind !== 'belongsTo' && field.kind !== 'hasMany') {
      continue;
    }

    // Collect all related record keys
    const relatedKeys = new Set<ResourceKey>();

    if (field.kind === 'hasMany') {
      const relationship = store.cache.getRelationship(
        cacheKey,
        fieldName,
        true
      ) as CollectionRelationship;
      if (relationship.data) {
        for (const relatedItemKey of relationship.data) {
          relatedKeys.add(relatedItemKey);
        }
      }
    } else if (field.kind === 'belongsTo') {
      const relationship = store.cache.getRelationship(
        cacheKey,
        fieldName,
        false
      ) as ResourceRelationship;
      if (relationship.data) {
        relatedKeys.add(relationship.data);
      }
    }

    // Recursively check each related record
    for (const relatedKey of relatedKeys) {
      try {
        const relatedRecord = store.peekRecord(relatedKey);
        if (relatedRecord) {
          const hasChanges = deepHasChanged(relatedRecord, store, {
            maxDepth: maxDepth - 1,
            visited,
          });
          if (hasChanges) {
            return true;
          }
        }
      } catch (error) {
        // If we can't peek the record, it may have been unloaded
        // Continue processing other records
        console.warn(
          `Could not peek record ${relatedKey.type}:${relatedKey.id}`,
          error
        );
      }
    }
  }

  // No changes found in this record or its relations
  return false;
}
